/// <amd-module name="Artifacts/FeedSettings/components/UpstreamStatusCell" />

import * as Res from "Artifacts/FeedSettings/Resources";
import { IUpstreamSource } from "Artifacts/FeedSettings/types";
import { CenteredSpinner } from "Artifacts/Root/components/CenteredSpinner";
import { GenericModalId, IGenericModalProps } from "Artifacts/Root/components/GenericModal";
import { ReadyComponentState } from "Artifacts/Root/helpers/ComponentStateHelpers";
import { getAuditLogUrl } from "Artifacts/Root/Helpers/UrlHelper";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { ProtocolService } from "Artifacts/Root/protocols/services/ProtocolService";
import { IProtocol } from "Artifacts/Root/protocols/types";
import { connectDynamicModules } from "Artifacts/Root/redux";
import { RootActions } from "Artifacts/Root/Redux/Actions";
import { ICurrentModal } from "Artifacts/Root/redux/Contracts";
import { RootModule } from "Artifacts/Root/Redux/Module";
import { IFeed } from "Artifacts/Root/types/IFeed";
import {
  UpstreamHealthStatus,
  UpstreamStatusCategory,
} from "Artifacts/Root/web-apis/contracts/WebPage";
import * as React from "react";
import { connect } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import * as Utils_String from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { useComponentContext } from "VSS/Platform/Layout";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { Link } from "VSSUI/Link";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import * as Utils_Date from "VSSUI/Utilities/Date";

export interface IUpstreamTableItem {
  protocol: IProtocol;
  /** Display name of upstream source */
  source: string;
  location: string;
  sourceType: string;
  /**
   * Include full upstream object for rendering sync status
   */
  upstreamSource: IUpstreamSource;
  status?: UpstreamHealthStatus;
}

const Actions = {
  showModal: RootActions.showModal,
  hideModal: RootActions.hideModal,
};

const UpstreamFailureStatusModalName = "UpstreamFailureStatusModal";

interface IUpstreamStatusCellOwnProps {
  tableItem: IUpstreamTableItem;
  columnIndex: number;
}

type IUpstreamStatusCellProps = IUpstreamStatusCellOwnProps & typeof Actions;

export const renderStatusCell = (
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IUpstreamTableItem>,
  tableItem: IUpstreamTableItem,
): JSX.Element => <ConnectedUpstreamStatusCell tableItem={tableItem} columnIndex={columnIndex} />;

export const UpstreamStatusCell: React.FC<IUpstreamStatusCellProps> = ({
  tableItem,
  columnIndex,
  showModal,
  hideModal,
}) => {
  const context = useComponentContext();
  const feed = useFeed().feed;
  const currentStatus = tableItem.status;
  if (!currentStatus) {
    // If a new upstream was added, display empty cell
    return <SimpleTableCell key={"col-" + columnIndex} columnIndex={columnIndex}></SimpleTableCell>;
  }

  // Partial status can only be populated if the date is newer than confident status
  const statusDate =
    currentStatus.confidentStatus != null && currentStatus.confidentStatus.timeStamp != null
      ? Utils_Date.ago(currentStatus.confidentStatus.timeStamp, Utils_Date.AgoFormat.Extended)
      : currentStatus.partialStatus != null && currentStatus.partialStatus.timeStamp != null
      ? Utils_Date.ago(currentStatus.partialStatus.timeStamp)
      : null;

  const statusProps = getStatusPropsFromStatus(currentStatus);

  return (
    <SimpleTableCell key={"col-" + columnIndex} columnIndex={columnIndex}>
      {statusDate && feed && statusProps !== Statuses.Success && (
        <Link
          key={"col-" + columnIndex}
          className="upstream-failing-status"
          onClick={() =>
            onStatusFailureLinkClick(
              context.pageContext,
              currentStatus,
              statusProps,
              tableItem.upstreamSource,
              tableItem.protocol.id,
              feed,
              (modalProps: IGenericModalProps) => {
                showModal(
                  {
                    modalId: GenericModalId,
                    props: modalProps,
                  } as ICurrentModal<IGenericModalProps>,
                  UpstreamFailureStatusModalName,
                );
              },
              hideModal,
            )
          }
          role="button"
          href="#"
          rel="noopener noreferrer"
          tooltipProps={{
            text: Res.UpstreamStatus_Failure + " " + Res.UpstreamStatus_FailureInfoClick,
          }}
        >
          <Status
            {...statusProps}
            size={StatusSize.s}
            className="upstream-status text-ellipsis"
            key={"col-" + columnIndex + "-status"}
          />
          <div className="status-text text-ellipsis">{statusDate}</div>
        </Link>
      )}
    </SimpleTableCell>
  );
};

function getStatusPropsFromStatus(currentStatus: UpstreamHealthStatus): IStatusProps {
  if (currentStatus.partialStatus != null && currentStatus.partialStatus.statusCategory != null) {
    return Statuses.Warning;
  } else if (
    currentStatus.confidentStatus &&
    currentStatus.confidentStatus.statusCategory === UpstreamStatusCategory.FullRefreshSuccess
  ) {
    return Statuses.Success;
  } else {
    return Statuses.Failed;
  }
}

function onStatusFailureLinkClick(
  pageContext: IVssPageContext,
  currentStatus: UpstreamHealthStatus,
  statusProps: IStatusProps,
  upstream: IUpstreamSource,
  protocolId: string,
  // currently only using this for publicAcccessMapping. Swap this for PAM instead of that's all that ends up being used
  feed: IFeed,
  showModal: (modalProps: IGenericModalProps) => void,
  hideModal: () => void,
): void {
  let dialogProps: Partial<IGenericModalProps> | null;
  if (statusProps === Statuses.Warning && currentStatus.partialStatus != null) {
    dialogProps = createHealthStatusDialog(
      pageContext,
      currentStatus.partialStatus.statusCategory,
      protocolId,
      upstream,
      feed,
    );
  } else if (statusProps === Statuses.Failed && currentStatus.confidentStatus != null) {
    dialogProps = createHealthStatusDialog(
      pageContext,
      currentStatus.confidentStatus.statusCategory,
      protocolId,
      upstream,
      feed,
    );
  } else {
    dialogProps = null;
  }

  if (dialogProps) {
    dialogProps.primaryButtonText = Res.UpstreamStatus_Dialog_PrimaryButtonText;
    dialogProps.onPrimaryClicked = hideModal;
    dialogProps.onDismiss = hideModal;
    dialogProps.primaryButtonComponentState = ReadyComponentState();
    showModal(dialogProps as IGenericModalProps);
  }
}

function createHealthStatusDialog(
  pageContext: IVssPageContext,
  category: UpstreamStatusCategory,
  protocolId: string,
  upstream: IUpstreamSource,
  // currently only using this for publicAcccessMapping. Swap this for PAM instead of that's all that ends up being used
  feed: IFeed,
): Partial<IGenericModalProps> | null {
  const pageData: IPageData | undefined = pageContext
    .getService<IVssPageService>("IVssPageService")
    .getData();

  if (pageData) {
    const publicAccessMapping = feed.accountMetadata.publicAccessMapping;
    const hostId = pageData.hostId;
    const isSameOrg = Utils_String.equals(
      hostId,
      upstream.internalUpstreamCollectionId || "",
      true,
    );
    switch (category) {
      case UpstreamStatusCategory.TargetFeedDeleted:
        return {
          title: Res.UpstreamStatus_Dialog_TargetFeedDeleted_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_TargetFeedDeleted_Message}
              <div className="upstream-status-attention">
                {Res.UpstreamStatus_Dialog_ActionHeading}
              </div>
              {getAuditLogLinkMessage(pageContext, isSameOrg, publicAccessMapping)}
              {getFeedPermissionsLinkMessage(upstream, isSameOrg)}
              {getReadMoreLinkMessage()}
            </div>
          ),
          warningCardContent: getWarningMessage(),
        };
      case UpstreamStatusCategory.TargetViewDeleted:
        return {
          title: Res.UpstreamStatus_Dialog_TargetViewDeleted_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_TargetViewDeleted_Message}
              <div className="upstream-status-attention">
                {Res.UpstreamStatus_Dialog_ActionHeading}
              </div>
              {getAuditLogLinkMessage(pageContext, isSameOrg, publicAccessMapping)}
              {getFeedPermissionsLinkMessage(upstream, isSameOrg)}
              {getReadMoreLinkMessage()}
            </div>
          ),
          warningCardContent: getWarningMessage(),
        };
      case UpstreamStatusCategory.TargetProjectDeleted:
        return {
          title: Res.UpstreamStatus_Dialog_TargetProjectDeleted_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_TargetProjectDeleted_Message}
              <div className="upstream-status-attention">
                {Res.UpstreamStatus_Dialog_ActionHeading}
              </div>
              {getAuditLogLinkMessage(pageContext, isSameOrg, publicAccessMapping)}
              {getReadMoreLinkMessage()}
            </div>
          ),
          warningCardContent: getWarningMessage(),
        };
      case UpstreamStatusCategory.TargetViewInsufficientVisibility:
        return {
          title: Res.UpstreamStatus_Dialog_TargetViewInsufficientVisibility_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_TargetViewInsufficientVisibility_Message}
              <div className="upstream-status-attention">
                {Res.UpstreamStatus_Dialog_ActionHeading}
              </div>
              {getAuditLogLinkMessage(pageContext, isSameOrg, publicAccessMapping)}
              {getFeedPermissionsLinkMessage(upstream, isSameOrg)}
              {getReadMoreLinkMessage()}
            </div>
          ),
          warningCardContent: getWarningMessage(),
        };
      case UpstreamStatusCategory.PublicUpstreamFailure:
        return {
          title: Res.UpstreamStatus_Dialog_PublicUpstreamFailure_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              <div>{Res.UpstreamStatus_Dialog_PublicUpstreamFailure_Message}</div>
              <Link underline href={getPublicStatusLink(pageContext, protocolId)}>
                {getPublicStatusLink(pageContext, protocolId)}
              </Link>
              {getReadMoreLinkMessage()}
            </div>
          ),
        };
      case UpstreamStatusCategory.CustomPublicUpstreamFailure:
        return {
          title: Res.UpstreamStatus_Dialog_CustomPublicUpstreamFailure_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_CustomPublicUpstreamFailure_Message}
              {getReadMoreLinkMessage()}
            </div>
          ),
          warningCardContent: getWarningMessage(),
        };
      case UpstreamStatusCategory.CustomPublicUpstreamFailure_DuplicatePackageVersions:
        return {
          title: Res.UpstreamStatus_Dialog_CustomPublicUpstreamFailure_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_CustomPublicUpstreamFailureDuplicatePackageVersion_Message}
              {getReadMoreLinkMessage()}
            </div>
          ),
          warningCardContent: getWarningMessage(),
        };
      case UpstreamStatusCategory.UnknownFailure:
        return {
          title: Res.UpstreamStatus_Dialog_UnknownFailure_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_UnknownFailure_Message}
              {getReadMoreLinkMessage()}
            </div>
          ),
        };
      case UpstreamStatusCategory.TargetOrganizationInaccessible:
        return {
          title: Res.UpstreamStatus_Dialog_TargetOrganizationInaccessible_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_TargetOrganizationInaccessible_Message}
              {getReadMoreLinkMessage()}
            </div>
          ),
          warningCardContent: getWarningMessage(),
        };
      case UpstreamStatusCategory.BlockedBySystem:
        return {
          title: Res.UpstreamStatus_Dialog_BlockedBySystem_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_BlockedBySystem_Message}
            </div>
          ),
          warningCardContent: getWarningMessage(),
        };
      case UpstreamStatusCategory.TargetOrganizationServiceConnectionFailure:
        return {
          title: Res.UpstreamStatus_Dialog_TargetOrganizationServiceConnectionFailure_Heading,
          body: (
            <div className="upstream-status-dialog-content">
              {Res.UpstreamStatus_Dialog_TargetOrganizationServiceConnectionFailure_Message}
              {getReadMoreLinkMessage()}
            </div>
          ),
          warningCardContent: getWarningMessage(),
        };
      default:
        return null;
    }
  } else {
    return null;
  }
}

function getWarningMessage(): JSX.Element {
  return (
    <FormatComponent format={"{0} {1}"}>
      {Res.UpstreamStatus_Dialog_WarningMessage_UserAction}
      {Res.UpstreamStatus_Dialog_WarningMessage}
    </FormatComponent>
  );
}

function getPublicStatusLink(pageContext: IVssPageContext, protocolId: string): string | undefined {
  const protocolService: ProtocolService = pageContext.getService("IProtocolService");
  const protocol = protocolService.getProtocolById(protocolId);
  return protocol.publicRepoStatusUrl;
}

function getAuditLogLinkMessage(
  pageContext: IVssPageContext,
  isSameOrg: boolean,
  publicAccessMapping: string,
) {
  // For the same org, we'll give a link to the audit log, for a different org, we'll give the link to audit log docs.
  const url = isSameOrg
    ? getAuditLogUrl(pageContext, publicAccessMapping)
    : "https://docs.microsoft.com/en-us/azure/devops/organizations/settings/azure-devops-auditing";

  return (
    <div className="dialog-link-area">
      <FormatComponent format={Res.UpstreamStatus_Dialog_AuditlogMessage}>
        <Link underline href={url}>
          {Res.UpstreamStatus_Dialog_AuditlogLink}
        </Link>
      </FormatComponent>
    </div>
  );
}

function getFeedPermissionsLinkMessage(upstream: IUpstreamSource, isSameOrg: boolean): JSX.Element {
  if (isSameOrg) {
    return (
      <div className="dialog-link-area">
        <FormatComponent format={Res.UpstreamStatus_Dialog_PermissionMessage}>
          {/** Make this a react router link to the permissions tab */}
          <RouterLink component={Link} to={"PlaceholderLink" + upstream.name}>
            {Res.UpstreamStatus_Dialog_PermissionLink}
          </RouterLink>
        </FormatComponent>
      </div>
    );
  } else {
    return (
      <div className="dialog-link-area">
        <FormatComponent format={Res.UpstreamStatus_Dialog_PermissionMessage}>
          {Res.UpstreamStatus_Dialog_PermissionLink}
        </FormatComponent>
      </div>
    );
  }
}

function getReadMoreLinkMessage() {
  return (
    <div className="dialog-link-area read-more-link-area">
      <FormatComponent format={Res.UpstreamStatus_Dialog_DocumentationMessage}>
        <Link underline href={"https://go.microsoft.com/fwlink/?linkid=2137532"}>
          {Res.UpstreamStatus_Dialog_DocumentationLink}
        </Link>
      </FormatComponent>
    </div>
  );
}

const ConnectedUpstreamStatusCell = connectDynamicModules<IUpstreamStatusCellOwnProps>(
  connect(null, Actions)(UpstreamStatusCell),
  /** For some reason root module exports a module and not an array of modules */
  [RootModule],
);
